// src/pages/auth/LoginPage.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import SignupModal from '../../components/auth/SignUpModal.jsx';

export default function LoginPage() {
    const { login, user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from;
    const [showSignup, setShowSignup] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const continueTo = (role) => {
        if (from?.pathname) {
            navigate(from.pathname + (from.search || ''), { replace: true });
        } else if (role === 'admin') {
            navigate('/admin');
        } else {
            navigate('/resume');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Call the actual PHP login script
            const response = await fetch('https://renaisons.com/api/login.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json', // Optional: Explicitly accept JSON
                },
                body: JSON.stringify({ email, password }),
                credentials: 'include',
            });

            const result = await response.json();

            if (response.ok && result.status === 'success') {
                login(result.user); // Update context with user data
                continueTo(result.user?.role);
            } else {
                setError(result.message || `Login failed with status: ${response.status}`);
            }
        } catch (err) {
            // Handle network errors or issues parsing JSON
            console.error("Login fetch error:", err);
            setError('Login request failed. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    // Already signed in — the login form is pointless; go home.
    if (!authLoading && user) {
        return <Navigate to={user.role === 'admin' ? '/admin' : '/resume'} replace />;
    }

    return (
        // No page-level background: the site's glowing body background shows
        // through, same as every other page under MainLayout.
        <div className="flex items-center justify-center px-4 py-16 sm:py-24 text-white">
            <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-8 shadow-2xl">
                <h1 className="mb-6 text-center text-2xl font-semibold">Sign In</h1>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm text-neutral-400 mb-1">Email</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent"
                            autoComplete="email"
                            placeholder="you@example.com"
                        />
                    </div>

                    <div>
                        <label className="block text-sm text-neutral-400 mb-1">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 text-sm text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-transparent"
                            autoComplete="current-password"
                            placeholder="Enter your password"
                        />
                    </div>

                    <div className="text-right pt-1">
                        <Link
                            to="/forgot-password"
                            className="text-sm text-cyan-300 hover:text-cyan-200 hover:underline"
                        >
                            Forgot Password?
                        </Link>
                    </div>

                    {error && (
                        <p className="text-red-400 text-sm text-center pt-2">{error}</p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2.5 transition-colors disabled:opacity-50 mt-4 shadow-lg shadow-cyan-500/10"
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-neutral-500">
                    Don’t have an account?{' '}
                    <button type="button" onClick={() => setShowSignup(true)} className="text-cyan-300 hover:text-cyan-200 hover:underline">
                        Sign up
                    </button>
                </p>
                <SignupModal
                    isOpen={showSignup}
                    onClose={() => setShowSignup(false)}
                    onSignupSuccess={(user) => continueTo(user?.role)}
                />
            </div>
        </div>
    );
}
